import { store, authActions } from "_store";
import { ERR_MSG, SESSION_EXPIRED } from "constants/labels";
import { authChannel } from "./authChannel";

export const fetchWrapper = {
  get: request("GET"),
  post: request("POST"),
  put: request("PUT"),
  delete: request("DELETE"),
  getFile,
  postFile
};

let refreshPromise = null;

function request(method) {
  return async (url, body) => {
    const requestOptions = buildRequestOptions(method, url, body);

    try {
      const response = await fetch(url, requestOptions);
      return await handleResponse(response, { url, requestOptions });
    } catch (err) {
      return Promise.reject(err);
    }
  };
}

// helper functions

function buildRequestOptions(method, url, body) {
  const headers = authHeader(url);

  const requestOptions = {
    method,
    headers,
    credentials: "include",
  };

  if (body?.Data instanceof FormData) {
    requestOptions.body = body.Data;
  } else if (body) {
    requestOptions.headers["Content-Type"] = "application/json";
    requestOptions.body = JSON.stringify(body);
  }

  return requestOptions;
}

async function getFile(url) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(url),
    credentials: "include",
  };
  const response = await fetch(url, requestOptions);

  if (response.status === 401) {
    await refreshAccessToken();
    response = await fetch(url, requestOptions);
  }

  if (!response.ok) {
    throw new Error(ERR_MSG["073"]);
  }

  const blob = await response.blob();

  return {
    blob,
    headers: response.headers,
  };
}

async function postFile(url, body) {
  const requestOptions = {
    method: "POST",
    headers: {
      ...authHeader(url),
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  };
  const response = await fetch(url, requestOptions);

  if (response.status === 401) {
    await refreshAccessToken();
    response = await fetch(url, requestOptions);
  }

  if (!response.ok) {
    throw new Error(ERR_MSG["073"]);
  }
  const blob = await response.blob();

  return {
    blob,
    headers: response.headers,
  };
}

function authHeader(url) {
  const token = authToken();
  const isLoggedIn = !!token;

  const isApiUrl =
    url.startsWith(process.env.REACT_APP_API_URL) ||
    url.startsWith(process.env.REACT_APP_MARKETER_API_URL) ||
    url.startsWith(process.env.REACT_APP_API_URL_AccountInquery) ||
    url.startsWith(process.env.REACT_APP_API_URL_ENERGY_ASSISTANCE) ||
    url.startsWith(process.env.REACT_APP_API_URL_MAP_CENTER) ||
    url.startsWith(process.env.REACT_APP_API_URL_SUPPLIER_DIVERISTY);
  if (isLoggedIn && isApiUrl) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

function authToken() {
  const { accessToken } = store.getState().auth;
  if (accessToken) return accessToken;

  const registrationToken = store.getState().registration?.verifiedUserData?.Data?.jwToken;
  if (registrationToken) return registrationToken;

  const publicToken = store.getState().faq?.publicToken;
  return publicToken ?? null;
}

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = fetch(
      `${process.env.REACT_APP_API_URL}/api/Account/Refresh`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      },
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(ERR_MSG["073"]);
        return res.json();
      })
      .then((data) => {
        const newAccessToken = data?.Data?.AccessToken;
        if (!newAccessToken) {
          throw new Error(SESSION_EXPIRED);
        }
        store.dispatch(authActions.assignAccessToken(newAccessToken));

        return newAccessToken;
      })
      .catch((error) => {
        authChannel.postMessage({ type: "SESSION_EXPIRED" });
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

async function handleResponse(response, context) {
  const isJson = response.headers
    ?.get("content-type")
    ?.includes("application/json");
  const data = isJson ? await response.json() : null;

  if (response.ok) return data;

  const isResponse401 = response.status === 401;
  const hasRetried = context?.requestOptions?._retry;

  if (isResponse401 && !hasRetried) {
    context.requestOptions._retry = true;

    try {
      const newToken = await refreshAccessToken();
      if (!newToken) throw new Error(SESSION_EXPIRED);

      context.requestOptions.headers = {
        ...context.requestOptions.headers,
        Authorization: `Bearer ${newToken}`,
      };

      const retryResponse = await fetch(context.url, context.requestOptions);
      return await handleResponse(retryResponse, context);
    } catch (error) {
      store.dispatch(authActions.clear());
      return Promise.reject(error);
    }
  }

  if (response.status === 400) {
    const errors = data?.errors || data?.Message || ERR_MSG["073"];
    return Promise.reject(errors);
  }

  /** Catch other http errors (500, 403, 404 etc.) */
  return Promise.reject(ERR_MSG["073"]);
}
